export type FinancialRpcErrorCategory =
  | 'authentication'
  | 'authorization'
  | 'rpc_unavailable'
  | 'schema_mismatch'
  | 'network'
  | 'unknown';

export interface FinancialRpcErrorInput {
  code?: string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
  status?: number | null;
  statusText?: string | null;
}

export interface FinancialRpcFailure extends Required<Omit<FinancialRpcErrorInput, 'status'>> {
  category: FinancialRpcErrorCategory;
  status: number | null;
  diagnosticId: string;
  userMessage: string;
  retryAfterTokenRefresh: boolean;
}

const clean = (value: string | null | undefined): string => value?.trim() ?? '';

export function isFinancialRpcAbort(input: FinancialRpcErrorInput, signalAborted = false): boolean {
  if (signalAborted) return true;
  const searchable = `${clean(input.message)} ${clean(input.details)} ${clean(input.hint)}`;
  return input.status === 0 && /aborterror|signal is aborted|request aborted/i.test(searchable);
}

const createDiagnosticId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `financial-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export function normalizeFinancialRpcError(
  input: FinancialRpcErrorInput,
  diagnosticId = createDiagnosticId(),
): FinancialRpcFailure {
  const code = clean(input.code);
  const message = clean(input.message);
  const details = clean(input.details);
  const hint = clean(input.hint);
  const statusText = clean(input.statusText);
  const status = typeof input.status === 'number' ? input.status : null;
  const searchable = `${message} ${details} ${hint} ${statusText}`;

  if (status === 401 || code === 'PGRST301' || /jwt|token.*expir|invalid.*token/i.test(searchable)) {
    return {
      category: 'authentication', code, message, details, hint, statusText, status, diagnosticId,
      retryAfterTokenRefresh: true,
      userMessage: `Sua sessão de acesso ao financeiro não pôde ser validada. Entre novamente e informe o diagnóstico ${diagnosticId}.`,
    };
  }
  if (code === '42501' || status === 403 || /permission denied|acesso financeiro não autorizado/i.test(searchable)) {
    return {
      category: 'authorization', code, message, details, hint, statusText, status, diagnosticId,
      retryAfterTokenRefresh: false,
      userMessage: `Seu usuário não possui acesso financeiro administrativo a esta clínica. Diagnóstico: ${diagnosticId}.`,
    };
  }
  if (code === 'PGRST202' || code === 'PGRST203' || code === '42883'
    || (status === 404 && /function|schema cache|rpc/i.test(searchable))) {
    return {
      category: 'rpc_unavailable', code, message, details, hint, statusText, status, diagnosticId,
      retryAfterTokenRefresh: false,
      userMessage: `A RPC financeira não está disponível no cache da API. Execute o runbook A0 e informe o diagnóstico ${diagnosticId}.`,
    };
  }
  if (code === '42P01' || code === '42703') {
    return {
      category: 'schema_mismatch', code, message, details, hint, statusText, status, diagnosticId,
      retryAfterTokenRefresh: false,
      userMessage: `A RPC financeira está incompatível com o schema implantado. Diagnóstico: ${diagnosticId}.`,
    };
  }
  if (status === 0 || /failed to fetch|network|fetch failed/i.test(searchable)) {
    return {
      category: 'network', code, message, details, hint, statusText, status, diagnosticId,
      retryAfterTokenRefresh: false,
      userMessage: `Não foi possível conectar ao serviço financeiro. Verifique a rede e tente novamente. Diagnóstico: ${diagnosticId}.`,
    };
  }
  return {
    category: 'unknown', code, message, details, hint, statusText, status, diagnosticId,
    retryAfterTokenRefresh: false,
    userMessage: `Não foi possível consultar o financeiro. Informe o diagnóstico ${diagnosticId} ao suporte.`,
  };
}
